import { Divider, MenuItem } from "@material-ui/core";
import React from "react";
import styled from "styled-components";

import { ICollaborationInvite, IWorkspacePermission, WorkspacePermissionSubjectType, WorkspaceResourceFolderType, WorkspaceUserRole } from "common/interfaces";
import { Button } from "js/Components/Button";
import { Dropdown } from "js/Components/Dropdown";
import PresentationPermissionsController, { PresentationPermissionsControllerState } from "js/controllers/PresentationPermissionsController";
import WorkspaceController, { WorkspaceControllerState } from "js/controllers/WorkspaceController";
import getLogger, { LogGroup } from "js/core/logger";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import { auth } from "js/firebase/auth";
import { app } from "js/namespaces";
import Avatar, { FolderAvatar, TeamAvatar } from "js/react/components/Avatar";
import { ShowDialog, ShowDialogAsync, ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import { ResendInviteDialog } from "js/react/components/InviteInput/ResendInviteDialog";
import { FlexBox } from "js/react/components/LayoutGrid";
import { ManageTeamFolderMembersDialog } from "js/react/views/TeamResources/dialogs/TeamFolderDialogs";
import TransferOwnershipDialog from "js/react/views/PresentationSettings/dialogs/TransferOwnershipDialog";

const logger = getLogger(LogGroup.PERMISSIONS);

const Container = styled.div`
    margin-left: -20px;
    width: calc(100% + 40px);
    color: #333;
    position: relative;
    overflow-y: auto;
    min-height: 50px;
`;

const ResendInviteButton = styled(Button)`
    &&& {
        opacity: 0;
        transition: opacity 300ms;
    }
`;

const OpenManageFolderButton = styled(Button)`
    &&& {
        opacity: 0;
        transition: opacity 300ms;
    }
`;

const PermissionRowContainer = styled.div`
    display: grid;
    grid-template-columns: 3fr 1fr 1fr 5fr 1fr;
    align-items: center;
    height: 54px;

    padding: 0px 30px;
    border-top: 1px solid #f0f0f0;

    &:hover {
      background: #f0f0f0;

      ${ResendInviteButton} {
        opacity: 1;
      }

      ${OpenManageFolderButton} {
        opacity: 1;
      }

      .MuiSelect-root {
        background: #f0f0f0;
      }
    }
`;

const PermissionRowNameContainer = styled.div`
    white-space: nowrap;
    margin: 10px;

    .displayName {
        color: #222;
        font-weight: 600;
    }

    .email {
        font-size: 12px;
        color: #666;
        display: block;
    }
`;

const MutedGrayText = styled.div`
    min-width: 84px;
    text-transform: uppercase;
    font-size: 10px;
    color: #555;
`;

const GuestBadge = styled.div`
    padding: 4px 5px;
    border-radius: 3px;
    background: #FFF0E3;
    color: #FA901F;
    font-family: "Source Sans Pro";
    font-size: 11px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    margin-left: auto;
`;

function PermissionsList({ permissions, invites, canChangeOwnership }: PresentationPermissionsControllerState & { canChangeOwnership?: boolean }) {
    const groupPermissions = permissions.filter(p => p.subjectType === WorkspacePermissionSubjectType.USER_GROUP);

    const usersPermissions = permissions
        .filter(p => p.subjectType === WorkspacePermissionSubjectType.USER)
        .sort((a, b) => (a.owner ? 0 : 100) - (b.owner ? 0 : 100) + (a.write.object ? 0 : 10) - (b.write.object ? 0 : 10) + (a.read.object ? 0 : 1) - (b.read.object ? 0 : 1));

    return (<Container data-test-id="presentation-permissions-list">
        {usersPermissions.map((permission, idx) => (
            <UserPermissionRow key={idx} permission={permission} canChangeOwnership={canChangeOwnership} />
        ))}
        {invites.map((invite, idx) => (
            <InviteRow key={idx} invite={invite} />
        ))}
        {groupPermissions.map((permission, idx) => (
            <GroupPermissionRow key={idx} permission={permission} />
        ))}
    </Container>);
}

const UserPermissionRow = WorkspaceController.withInitializedState(PresentationPermissionsController.withInitializedState(
    function UserPermissionRow({ permission, permissions, canChangeOwnership, users }: { permission: IWorkspacePermission, canChangeOwnership?: boolean } & PresentationPermissionsControllerState & WorkspaceControllerState) {
        const user = users.find(u => u.uid === permission.subjectId);
        if (!user) {
            return null;
        }

        const selfIsOwner = !!permissions.find(p => p.subjectId === auth().currentUser.uid && p.subjectType === WorkspacePermissionSubjectType.USER)?.owner;

        const handleRemovePermissions = async () => {
            try {
                await withProgressDialog({
                    title: "Revoking access...",
                    action: () =>
                        PresentationPermissionsController.removePresentationPermissionsForUsers({
                            users: [{
                                uid: permission.subjectId
                            }]
                        })
                });
            } catch (err) {
                logger.error(err, "Failed to revoke access");

                ShowErrorDialog({
                    title: "Failed to revoke access",
                    message: err.message
                });
            }
        };

        const handleTransferOwnership = async () => {
            try {
                await withProgressDialog({
                    title: "Transferring ownership...",
                    action: async () => {
                        const shouldTransferOwnership = await ShowDialogAsync(TransferOwnershipDialog, {
                            newOwnerEmail: user.email
                        });
                        if (!shouldTransferOwnership) {
                            return;
                        }

                        await PresentationPermissionsController.updatePresentationPermissionsForUsers({
                            users: [{
                                uid: permission.subjectId,
                                email: user.email,
                                permissions: {
                                    write: { object: true, permissions: true },
                                    read: { object: true, permissions: true },
                                    owner: true
                                }
                            }]
                        });
                    }
                });
            } catch (err) {
                logger.error(err, "Failed to transfer ownership");

                ShowErrorDialog({
                    title: "Failed to transfer ownership",
                    message: err.message
                });
            }
        };

        const handleUpdatePermissions = async (update: "edit" | "view") => {
            try {
                await withProgressDialog({
                    title: "Updating permissions...",
                    action: async () => {
                        return PresentationPermissionsController.updatePresentationPermissionsForUsers({
                            users: [{
                                uid: permission.subjectId,
                                email: user.email,
                                permissions: {
                                    write: { object: update === "edit", permissions: update === "edit" },
                                    read: { object: true, permissions: true },
                                    owner: false
                                }
                            }]
                        });
                    }
                });
            } catch (err) {
                logger.error(err, "Failed to update permissions");

                ShowErrorDialog({
                    title: "Failed to update permissions",
                    message: err.message
                });
            }
        };

        const handlePermissionDropdownChange = (update: "edit" | "view" | "remove" | "owner") => {
            if (update === "remove") {
                handleRemovePermissions();
            } else if (update === "owner") {
                handleTransferOwnership();
            } else {
                handleUpdatePermissions(update);
            }
        };

        const isSelf = user.uid === auth().currentUser.uid;

        return (<PermissionRowContainer data-test-id="presentation-permissions-list-row-user">
            <FlexBox left>
                <Avatar url={user.photoURL} />
                <PermissionRowNameContainer data-test-id="name">
                    {user.displayName && <span className="displayName">{user.displayName}</span>}
                    <span className="email">{user.email}</span>
                </PermissionRowNameContainer>
                {user.role === WorkspaceUserRole.GUEST &&
                    <GuestBadge>Guest</GuestBadge>
                }
            </FlexBox>
            <FlexBox left>
                {permission.owner &&
                    <Dropdown
                        value={"owner"}
                        variant="standard"
                        disableUnderline
                        disabled
                        data-test-id="permission-dropdown"
                    >
                        <MenuItem value={"owner"}>Owner</MenuItem>
                    </Dropdown>
                }
                {!permission.owner &&
                    <Dropdown
                        value={permission.write.object ? "edit" : "view"}
                        variant="standard"
                        disableUnderline
                        disabled={isSelf}
                        onChange={handlePermissionDropdownChange}
                        data-test-id="permission-dropdown"
                    >
                        <MenuItem value={"edit"}>Can Edit</MenuItem>
                        <MenuItem value={"view"}>Can View</MenuItem>
                        {selfIsOwner && canChangeOwnership &&
                            <MenuItem value={"owner"}>Make Owner</MenuItem>
                        }
                        <Divider />
                        <MenuItem value={"remove"}>Revoke Access</MenuItem>
                    </Dropdown>
                }
            </FlexBox>
            <FlexBox left>
                {isSelf &&
                    <MutedGrayText>You</MutedGrayText>
                }
            </FlexBox>
            <FlexBox></FlexBox>
            <FlexBox></FlexBox>
        </PermissionRowContainer>);
    }
));

const GroupPermissionRow = WorkspaceController.withInitializedState(
    function GroupPermissionRow({ permission, userGroups, resourceFolders }: { permission: IWorkspacePermission } & WorkspaceControllerState) {
        const group = userGroups.find(g => g.id === permission.subjectId);
        if (!group) {
            return null;
        }

        const folder = resourceFolders.find(f => f.userGroupId === group.id);
        if (!folder) {
            return null;
        }

        const handleOpenViewMembers = () => {
            ShowDialog(ManageTeamFolderMembersDialog, {
                folderId: folder.id,
                viewOnly: true
            });
        };

        const handleUpdatePermissions = async (update: "edit" | "view") => {
            try {
                await withProgressDialog({
                    title: "Updating permissions...",
                    action: () => PresentationPermissionsController.updatePresentationPermissionsForFolder({
                        folderId: folder.id,
                        presentationPermissions: {
                            write: { object: update === "edit", permissions: update === "edit" },
                            read: { object: true, permissions: true }
                        }
                    })
                });
            } catch (err) {
                logger.error(err, "Failed to update permissions");

                ShowErrorDialog({
                    title: "Failed to update permissions",
                    message: err.message
                });
            }
        };

        const handleRemovePermissions = async () => {
            try {
                await withProgressDialog({
                    title: "Removing presentation from folder...",
                    action: () => PresentationPermissionsController.removePresentationPermissionsForFolder({
                        folderId: folder.id
                    })
                });
            } catch (err) {
                logger.error(err, "Failed to remove presentation from folder");

                ShowErrorDialog({
                    title: "Failed to remove presentation from folder",
                    message: err.message
                });
            }
        };

        const handlePermissionDropdownChange = (update: "edit" | "view" | "remove") => {
            if (update === "remove") {
                handleRemovePermissions();
            } else {
                handleUpdatePermissions(update);
            }
        };

        const isDefault = folder.folderType === WorkspaceResourceFolderType.SHARED_DEFAULT;

        return (<PermissionRowContainer data-test-id="presentation-permissions-list-row-group">
            <FlexBox left>
                {isDefault ? <TeamAvatar /> : <FolderAvatar />}
                <PermissionRowNameContainer data-test-id="name">
                    <span className="displayName">{group.name}</span>
                </PermissionRowNameContainer>
            </FlexBox>
            <FlexBox left>
                <Dropdown
                    value={permission.write.object ? "edit" : "view"}
                    variant="standard"
                    disableUnderline
                    onChange={handlePermissionDropdownChange}
                    data-test-id="permission-dropdown"
                >
                    <MenuItem value={"edit"}>Can Edit</MenuItem>
                    <MenuItem value={"view"}>Can View</MenuItem>
                    <Divider />
                    <MenuItem value={"remove"}>Remove From Folder</MenuItem>
                </Dropdown>
            </FlexBox>
            <FlexBox left>
                <OpenManageFolderButton unframed onClick={handleOpenViewMembers}>view members</OpenManageFolderButton>
            </FlexBox>
            <FlexBox></FlexBox>
            <FlexBox></FlexBox>
        </PermissionRowContainer>);
    });

function InviteRow({ invite }: { invite: ICollaborationInvite }) {
    const handleUpdatePermissions = async (update: "edit" | "view") => {
        try {
            await withProgressDialog({
                title: "Updating invite...",
                action: () => PresentationPermissionsController.updatePresentationPermissionsForUsers({
                    users: [{
                        uid: invite.inviteeUid,
                        email: invite.inviteeEmail,
                        permissions: {
                            write: { object: update === "edit", permissions: update === "edit" },
                            read: { object: true, permissions: true },
                            owner: false
                        }
                    }]
                })
            });
        } catch (err) {
            logger.error(err, "Failed to update invite");

            ShowErrorDialog({
                title: "Failed to update invite",
                message: err.message
            });
        }
    };

    const handleRemovePermissions = async () => {
        try {
            await withProgressDialog({
                title: "Revoking invite...",
                action: () => PresentationPermissionsController.removePresentationPermissionsForUsers({
                    users: [{
                        uid: invite.inviteeUid,
                        email: invite.inviteeEmail
                    }]
                })
            });
        } catch (err) {
            logger.error(err, "Failed to remove invite");

            ShowErrorDialog({
                title: "Failed to remove invite",
                message: err.message
            });
        }
    };

    const handlePermissionDropdownChange = (update: "edit" | "view" | "remove") => {
        if (update === "remove") {
            handleRemovePermissions();
        } else {
            handleUpdatePermissions(update);
        }
    };

    const handleResendInvite = () => {
        ShowDialog(ResendInviteDialog, { invite });
    };

    return (<PermissionRowContainer data-test-id="presentation-permissions-list-row-invite">
        <FlexBox left>
            <Avatar url={null} />
            <PermissionRowNameContainer data-test-id="name">
                <span className="email">{invite.inviteeEmail}</span>
            </PermissionRowNameContainer>
        </FlexBox>
        <FlexBox left>
            <Dropdown
                value={invite.permissions.write ? "edit" : "view"}
                variant="standard"
                disableUnderline
                onChange={handlePermissionDropdownChange}
                data-test-id="permission-dropdown"
            >
                <MenuItem value={"edit"}>Can Edit</MenuItem>
                <MenuItem value={"view"}>Can View</MenuItem>
                <Divider />
                <MenuItem value={"remove"}>Revoke Invite</MenuItem>
            </Dropdown>
        </FlexBox>
        <FlexBox left>
            <MutedGrayText>pending signup</MutedGrayText>
        </FlexBox>
        <FlexBox></FlexBox>
        <FlexBox right>
            {!app.isConstrained &&
                <ResendInviteButton blue onClick={handleResendInvite}>Resend Invite</ResendInviteButton>
            }
        </FlexBox>
    </PermissionRowContainer >);
}

export default PresentationPermissionsController.withInitializedState(PermissionsList);
